import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { deleteOwnAccount } from "@/lib/account.functions";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/profile")({
  head: () => ({ meta: [{ title: "Profilim — JUËN" }] }),
  component: ProfilePage,
});

function ProfilePage() {
  const { user, profile, loading, refreshProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteAccountFn = useServerFn(deleteOwnAccount);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    instagram: "",
    city: "",
    bio: "",
  });

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (profile) {
      setForm({
        first_name: profile.first_name ?? "",
        last_name: profile.last_name ?? "",
        phone: profile.phone ?? "",
        instagram: profile.instagram ?? "",
        city: profile.city ?? "",
        bio: profile.bio ?? "",
      });
    }
  }, [profile]);

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    const { error } = await supabase.from("profiles").update(form).eq("id", user.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(t("account.profile.updated"));
    await refreshProfile();
  };

  const handleDelete = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    if (!accessToken) {
      toast.error(t("account.noSession"));
      return;
    }
    setDeleting(true);
    const res = await deleteAccountFn({ data: { accessToken } });
    setDeleting(false);
    if (!res.success) {
      toast.error(res.error || t("account.profile.deleteFailed"));
      return;
    }
    toast.success(t("account.deleted"));
    await signOut();
    void navigate({ to: "/" });
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="mb-2 text-3xl font-bold tracking-tight">{t("account.profile.title")}</h1>
        <p className="mb-8 text-sm text-muted-foreground">{t("account.profile.subtitle")}</p>

        <form
          onSubmit={handleSave}
          className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-card md:p-8"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="first_name">{t("account.profile.firstName")}</Label>
              <Input id="first_name" value={form.first_name} onChange={update("first_name")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">{t("account.profile.lastName")}</Label>
              <Input id="last_name" value={form.last_name} onChange={update("last_name")} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("account.profile.email")}</Label>
            <Input value={profile.email} disabled />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="phone">{t("account.profile.phone")}</Label>
              <Input id="phone" value={form.phone} onChange={update("phone")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">{t("account.profile.city")}</Label>
              <Input id="city" value={form.city} onChange={update("city")} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="instagram">{t("account.profile.instagram")}</Label>
            <Input id="instagram" value={form.instagram} onChange={update("instagram")} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio">{t("account.profile.bio")}</Label>
            <Textarea id="bio" rows={3} value={form.bio} onChange={update("bio")} />
          </div>

          <Button type="submit" disabled={saving} className="h-12 w-full">
            {saving ? t("account.profile.saving") : t("account.profile.saveChanges")}
          </Button>
        </form>

        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
          <h2 className="text-lg font-semibold text-destructive">{t("account.deleteAccountButton")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("account.profile.deleteDesc")}
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="mt-4" disabled={deleting}>
                <Trash2 className="mr-1.5 h-4 w-4" />
                {deleting ? t("account.deleting") : t("account.deleteAccountButton")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("account.deleteConfirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("account.profile.deleteConfirmDesc")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("account.giveUp")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {t("account.confirmDelete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
